import React, { useState, useEffect } from 'react';

import { Badge, Button, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';

import Container from 'components/shared/Container';
import { formatCurrencyWithCommas } from 'lib/utils/FormatUtils';
import {
  GLOBAL_MANUFACTURERS_HARVEST,
  GLOBAL_MANUFACTURER_HARVEST_EXPORT,
  GLOBAL_MANUFACTURER_HARVEST_STATUS,
  GLOBAL_MANUFACTURER_HARVEST_TAGS_BY_MANUFACTURER,
  GLOBAL_MANUFACTURER_HARVEST_STATISTICS,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationBar from 'components/shared/PaginationBar';
import { SORT_DIRECTIONS } from 'lib/constants';
import StatusDropdown from './StatusDropdown';
import StatusConfirmationModal from './StatusConfirmationModal';
import FilterBar from './FilterBar';
import WidgetHeader from './WidgetHeader';
import TagList from 'components/shared/TagList';
import TagEditor from 'components/shared/TagEditor';
import { SwalToast } from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePatch from 'lib/hooks/usePatch';
import usePost from 'lib/hooks/usePost';

const PAGE_SIZE = 100;

function GlobalManufacturerHarvestView() {
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTagFilters, setSelectedTagFilters] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [debouncedSearchName] = useDebounce(searchName, 300);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState(null);
  const offset = currentPage * PAGE_SIZE;

  const tagFilterValues = selectedTagFilters.map(option => option.value);
  const trimmedSearchName = debouncedSearchName.trim();

  const harvestUrlParams = new URLSearchParams({
    limit: PAGE_SIZE,
    offset: offset,
    sort_by_gmv: SORT_DIRECTIONS.DESC,
  });
  tagFilterValues.forEach(tag => harvestUrlParams.append('tags', tag));
  if (trimmedSearchName) {
    harvestUrlParams.append('name', trimmedSearchName);
  }
  const harvestUrl = `${GLOBAL_MANUFACTURERS_HARVEST}?${harvestUrlParams.toString()}`;

  const {
    data: { global_manufacturer_harvests = [], total_count = 0 } = {},
    loading,
    isLoaded,
    refetch: refetchHarvests,
  } = useGet(harvestUrl);

  const {
    data: statisticsData = {},
    loading: statsLoading,
    refetch: refetchStats,
  } = useGet(GLOBAL_MANUFACTURER_HARVEST_STATISTICS);

  const { patchData: patchStatus, loading: isUpdatingStatus } = usePatch(
    selectedManufacturer?.global_manufacturer_harvest?.id
      ? GLOBAL_MANUFACTURER_HARVEST_STATUS(selectedManufacturer.global_manufacturer_harvest.id)
      : null,
    () => {
      SwalToast('success', 'Status updated successfully');
      refetchHarvests();
      refetchStats();
      setShowModal(false);
    },
    error => {
      SwalToast('error', error?.response?.data?.error || 'Failed to update status');
    },
  );

  const { postData: exportHarvest, loading: isExporting } = usePost(
    GLOBAL_MANUFACTURER_HARVEST_EXPORT,
    () => {
      SwalToast('success', 'Export started successfully. Please check the file exports page.');
    },
    error => {
      SwalToast('error', error?.response?.data?.error || 'Failed to start export');
    },
  );

  const { patchData: patchTags, loading: isUpdatingTags } = usePatch(
    editingManufacturer?.id
      ? GLOBAL_MANUFACTURER_HARVEST_TAGS_BY_MANUFACTURER(editingManufacturer.id)
      : null,
    () => {
      SwalToast('success', 'Tags updated successfully');
      refetchHarvests();
      refetchStats();
      setShowTagEditor(false);
      setEditingManufacturer(null);
    },
    error => {
      SwalToast('error', error?.response?.data?.error || 'Failed to update tags');
    },
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedTagFilters, debouncedSearchName]);

  if (loading && !isLoaded) {
    return <LoadingSpinner />;
  }

  const totalPages = Math.ceil((total_count || 0) / PAGE_SIZE);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleStatusSelect = (manufacturer, newStatus) => {
    setSelectedManufacturer(manufacturer);
    setSelectedStatus(newStatus);
    setShowModal(true);
  };

  const handleConfirmStatusChange = () => {
    if (!selectedManufacturer?.global_manufacturer_harvest?.id) {
      SwalToast('error', 'No harvest record found for this manufacturer');
      setShowModal(false);
      return;
    }

    patchStatus({ status: selectedStatus });
  };

  const handleCloseModal = () => {
    if (!isUpdatingStatus) {
      setShowModal(false);
      setSelectedManufacturer(null);
      setSelectedStatus(null);
    }
  };

  const handleTagsChange = selectedOptions => {
    setSelectedTagFilters(selectedOptions);
  };

  const handleSearchNameChange = value => {
    setSearchName(value);
  };

  const handleClearFilters = () => {
    setSelectedTagFilters([]);
    setSearchName('');
  };

  const handleEditTags = manufacturer => {
    setEditingManufacturer(manufacturer);
    setShowTagEditor(true);
  };

  const handleSaveTags = tags => {
    if (!editingManufacturer?.id) {
      SwalToast('error', 'Invalid manufacturer selected');
      setShowTagEditor(false);
      return;
    }
    patchTags({ tags });
  };

  const handleCloseTagEditor = () => {
    if (!isUpdatingTags) {
      setShowTagEditor(false);
      setEditingManufacturer(null);
    }
  };

  const hasActiveFilters = selectedTagFilters.length > 0 || searchName.trim().length > 0;

  return (
    <Container className={css(styles.container)}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="font-weight-bold">Global Manufacturer Harvest</h3>
        <Button variant="outline-primary" onClick={() => exportHarvest()} disabled={isExporting}>
          <FontAwesomeIcon icon={faDownload} className="mr-2" />
          Export CSV
        </Button>
      </div>

      <WidgetHeader stats={statisticsData} loading={statsLoading} />

      <FilterBar
        selectedTags={selectedTagFilters}
        onTagsChange={handleTagsChange}
        searchName={searchName}
        onSearchNameChange={handleSearchNameChange}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      <div className="d-flex justify-content-end mb-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>

      <Table className="mt-2">
        <thead>
          <tr>
            <th>Manufacturer Name</th>
            <th>Is Engaged</th>
            <th>GMV (L30 Annualized)</th>
            <th>Tags</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                Loading...
              </td>
            </tr>
          )}
          {!loading && global_manufacturer_harvests.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                No manufacturers match the current filters.
              </td>
            </tr>
          )}
          {!loading &&
            global_manufacturer_harvests.map(record => (
              <tr key={record.id}>
                <td>{record.name}</td>
                <td>
                  {record.engaged ? (
                    <Badge variant="success">YES</Badge>
                  ) : (
                    <Badge variant="danger">NO</Badge>
                  )}
                </td>
                <td>{formatCurrencyWithCommas(record?.global_manufacturer_harvest?.gmv)}</td>
                <td>
                  <TagList
                    tags={record?.global_manufacturer_harvest?.tags || []}
                    maxVisible={3}
                    editable={true}
                    onEdit={() => handleEditTags(record)}
                  />
                </td>
                <td>
                  <StatusDropdown
                    currentStatus={record?.global_manufacturer_harvest?.status}
                    onStatusSelect={newStatus => handleStatusSelect(record, newStatus)}
                    recordId={record.id}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
      <StatusConfirmationModal
        show={showModal}
        onHide={handleCloseModal}
        onConfirm={handleConfirmStatusChange}
        manufacturerName={selectedManufacturer?.name}
        newStatus={selectedStatus}
        isLoading={isUpdatingStatus}
      />
      <TagEditor
        show={showTagEditor}
        onHide={handleCloseTagEditor}
        currentTags={editingManufacturer?.global_manufacturer_harvest?.tags || []}
        onSave={handleSaveTags}
        isLoading={isUpdatingTags}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '150vh',
    overflowX: 'hidden',
  },
});

export default GlobalManufacturerHarvestView;
